import { pct } from '@shared/format';
import type { ClueProblem, ErrorCode } from '@shared/types';

/** Все тексты интерфейса в одном месте — задел под локализацию UI. */
export const t = {
  appName: 'Телепат',
  bomb: 'Бомба',
  cluePlaceholder: { ru: 'Подсказка одним словом', en: 'One-word clue' },
  submit: 'Загадать',
  thinking: 'ИИ думает…',
  emptyTip:
    'Придумай **одно слово**, которое сближает твои зелёные слова, — ИИ откроет то, что ближе всего к нему.',
  goal: (found: number, total: number) => `Найдено ${found} из ${total}`,
  record: 'Новый рекорд!',
  turnsCount: (n: number) => `Ходов: ${n}`,
  guessed: '✓ Угадано!',
  guessedHeard: (p: string) => `✓ Угадано! Но бомба услышала подсказку: +${p}`,
  pickedDecoy: (word: string, heard: string | null = null) =>
    `✗ ИИ выбрал обманку «${word}».${heard ? ` И бомба услышала подсказку: +${heard}.` : ''}`,
  heardNote: 'бомба услышала подсказку',
  loading: 'Загружаю…',
  retry: 'Повторить',
  loadFailed: 'Не удалось загрузить игру.',
  noBoards: 'Пока нет полей для этого языка.',

  win: { perfect: '🧠 Чистая телепатия!', normal: '🧠 Телепатия удалась' },
  lose: '💥 Бомба взорвалась',
  winSub: (heat: number) =>
    heat > 0 ? `Бомба нагрета на ${pct(heat)}` : 'Ни одной ошибки — бомба осталась холодной',
  loseSub: { picked: 'ИИ открыл бомбу', heat: 'Шкала бомбы дошла до 100%' },
  share: 'Поделиться',
  shared: 'Скопировано ✓',
  again: 'Ещё раз',
  nextBoard: 'Следующее поле',
  howToPlay: 'Как можно было сыграть',
  bestPath: (heat: number) => `Лучший путь, найденный ИИ — бомба ${pct(heat)}`,
  traps: 'Ловушки этого поля',
  explosion: 'взрыв',
  decoyMark: 'обманка',

  /** Строка под названием поля: узкая (≈135 px на телефоне), поэтому коротко — подробности на карте полей. */
  boardMeta: (k: number, n: number, generated: boolean, stars: number, passed = false) =>
    `${k} из ${n}${generated ? ' · ✨' : ''}${stars ? ` · ${'★'.repeat(stars)}` : passed ? ' · ✓ пройдено' : ''}`,

  card: {
    board: 'Поле',
    win: 'Телепатия удалась',
    perfect: 'Чистая телепатия!',
    lose: 'Бомба взорвалась',
    heat: (p: string) => `Бомба нагрета на ${p}`,
    overheated: 'Шкала бомбы дошла до 100%',
    picked: 'ИИ открыл бомбу',
    cta: 'Сможешь лучше?',
  },
  shareTitle: 'Телепат — словесная игра с ИИ',
  saveImage: 'Сохранить картинку',

  levels: {
    title: 'Поля',
    open: 'Открыть список полей',
    passed: (k: number, n: number) => `Пройдено ${k} из ${n}`,
    empty: 'Пока нет полей.',
    close: 'Закрыть',
    /** Подпись плитки для скринридера. */
    label: (k: number, title: string, stars: number, passed: boolean) =>
      `Поле ${k}: ${title}${passed ? (stars ? `, пройдено на ${stars} из 3 звёзд` : ', пройдено') : ', не пройдено'}`,
  },

  help: {
    title: 'Как играть',
    skip: 'Пропустить',
    back: 'Назад',
    next: 'Дальше',
    play: 'Играть',
    progress: (k: number, n: number) => `Шаг ${k} из ${n}`,
    steps: [
      {
        title: 'Ты видишь всё, ИИ — ничего',
        text: 'Зелёные слова — **твои**, 💣 — **бомба**, остальные — **обманки**. ИИ видит те же слова, но не знает, где какие.',
      },
      {
        title: 'Подскажи одним словом',
        text: 'Напиши слово-намёк на свои слова. ИИ откроет **одно** слово — то, что подходит лучше всего.',
      },
      {
        title: 'Бомба слышит каждую подсказку',
        text: 'Чем больше подсказка похожа на бомбу, тем сильнее она нагревается — **даже если ИИ угадал верно**. «Клык» — про волка, но и про вампира. А если ИИ откроет саму бомбу — **сразу взрыв**.',
      },
      {
        title: 'Обманки греют сильнее',
        text: 'Каждая обманка, которую откроет ИИ, добавляет ещё **25%**. Шкала дошла до 100% — **взрыв**.',
      },
      {
        title: 'Открой все 4 зелёных слова',
        text: 'Чем холоднее бомба в конце, тем больше звёзд. Подсказывай так, чтобы ИИ выбрал твоё слово и не думал о бомбе.',
        rules: 'Нельзя: слова с поля и однокоренные к ним.',
      },
    ],
    stars: [
      { stars: '★★★', label: 'до 25%' },
      { stars: '★★', label: 'до 50%' },
      { stars: '★', label: 'меньше 100%' },
    ],
  },
  settings: {
    title: 'Настройки',
    lang: 'Язык слов',
    save: 'Сохранить',
    close: 'Закрыть',
    note: 'Язык, рекорды и прогресс хранятся на этом устройстве.',
  },
  gen: {
    title: '✨ Придумываю новое поле',
    steps: ['Подбираю слова и ловушки', 'Проверяю, что поле честное и коварное', 'Готово'],
    initialNote: 'Обычно это занимает до минуты.',
    elapsed: (s: number) => `ИИ придумывает поля… ${s} с (обычно 30–60 с)`,
    close: 'Закрыть',
  },
} as const;

const clueProblemText = (p: ClueProblem | undefined): string => {
  switch (p?.code) {
    case 'empty':
      return 'Напиши подсказку.';
    case 'not_one_word':
      return 'Только одно слово — без пробелов и цифр.';
    case 'same_root':
      return `Слишком похоже на «${p.word}» — попробуй другое слово.`;
    default:
      return 'Эта подсказка не подходит.';
  }
};

export const clueProblemMessage = clueProblemText;

const ERROR_TEXT: Record<ErrorCode | 'network', string> = {
  invalid_request: 'Что-то пошло не так. Попробуй ещё раз.',
  not_found: 'Эта партия потерялась — начали такую же заново.',
  clue_invalid: 'Эта подсказка не подходит.',
  clue_used: 'Эта подсказка уже была.',
  clue_too_similar: 'Слишком похоже на одно из слов поля — попробуй другое.',
  game_over: 'Эта партия уже закончена.',
  conflict: 'Ход не прошёл — попробуй ещё раз.',
  rate_limited: 'Слишком часто — подожди пару секунд.',
  generation_limit: 'Сегодня новых полей больше не будет — попробуй завтра.',
  generation_failed: 'Не получилось придумать честное поле. Попробуй ещё раз.',
  ai_unavailable: 'ИИ сейчас недоступен — попробуй чуть позже.',
  ai_timeout: 'ИИ задумался слишком надолго — попробуй ещё раз.',
  ai_not_configured: 'ИИ пока не настроен на сервере.',
  internal: 'Что-то пошло не так. Попробуй ещё раз.',
  network: 'Нет связи с сервером. Проверь интернет и попробуй ещё раз.',
};

export const errorMessage = (code: ErrorCode | 'network', problem?: ClueProblem): string =>
  code === 'clue_invalid' ? clueProblemText(problem) : ERROR_TEXT[code];
